import React, { useState, useEffect, useCallback } from 'react'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import QRCode from 'qrcode'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import { isIOS, isNonAmazonAndroid } from '../../environment/index'
import {
  URL_APPLE_APP_STORE,
  URL_ANDROID_APP_STORE,
  DEEP_LINK_LOGIN_WITH_CODE,
} from '../../services/url/constants'
import { createLoginCode } from '../../services/remote-login-code/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import Button, { TYPES } from '../Button/index'
import AppStoreLogo from './AppStoreLogo'
import GooglePlayLogo from './GooglePlayLogo'
import Sherpa, { TYPE_SMALL } from '../Sherpa/index'

const config = getConfig()
const branchKey = _get(config, ['features', 'appDownloadBanner', 'branchKey'])

export const MODES = {
  APP_STORE_BUTTONS_LIGHT: 'light',
  APP_STORE_BUTTONS_DARK: 'dark',
}

/**
 * App store buttons on mobile.
 * @param {Object} props - The props
 * @param {string} props.mode - The mode
 * @param {string} props.text - The text
 * @returns {JSX.Element}
 */

function AppStoreButtons (props) {
  const {
    auth,
    text,
    mode = MODES.APP_STORE_BUTTONS_LIGHT,
  } = props

  const [branchSdkLoaded, setBranchSdkLoaded] = useState(window && window.branch)
  const [initialized, setInitialized] = useState(false)
  const [linkUrl, setLinkUrl] = useState(null)
  const [deepLink, setDeepLink] = useState(null)
  const [qrCode, setQrCode] = useState(null)

  const isLoggedIn = getAuthIsLoggedIn(auth)
  const ios = isIOS()
  const android = isNonAmazonAndroid()

  const color = mode === MODES.APP_STORE_BUTTONS_LIGHT ? '#151515' : '#ffffff'

  const createDeepLink = useCallback(async () => {
    if (!isLoggedIn) {
      setDeepLink(null)
    }
    const { remoteLoginCode } = await createLoginCode({ auth, autoApprove: true, type: 'one-time' })
    setDeepLink(`${DEEP_LINK_LOGIN_WITH_CODE}/${remoteLoginCode}`)
  }, [auth, isLoggedIn])

  useEffect(() => {
    createDeepLink()
  }, [createDeepLink])

  useEffect(() => {
    if (typeof window === 'undefined' || window.branch) {
      return
    }
    if (branchSdkLoaded) {
      return
    }
    import('branch-sdk').then(() => {
      setBranchSdkLoaded(true)
    })
  }, [branchSdkLoaded])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.branch) {
      return
    }
    if (branchSdkLoaded && !initialized) {
      window.branch.init(branchKey, {
        no_journeys: true,
      }, (err) => {
        if (!err) {
          setInitialized(true)
        }
      })
    }
  }, [branchSdkLoaded, initialized])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.branch) {
      return
    }
    if (branchSdkLoaded && initialized && deepLink) {
      window.branch.link({
        data: {
          $deeplink_path: deepLink,
        },
      }, (err, link) => {
        if (err) {
          setLinkUrl(null)
        } else {
          setLinkUrl(link)
        }
      })
    }
  }, [branchSdkLoaded, initialized, deepLink])

  useEffect(() => {
    if (linkUrl && !android && !ios) {
      QRCode.toDataURL(linkUrl, { margin: 0 }).then((url) => {
        setQrCode(url)
      })
    }
  }, [linkUrl, android, ios])

  return (
    <div className="app-store-buttons">
      {!!text && (
        <p className={`app-store-buttons__text app-store-buttons__text--${mode}`}>
          {text}
        </p>
      )}
      {android && (
        <Button
          url={linkUrl || URL_ANDROID_APP_STORE}
          directLink
          className={`app-store-buttons__button app-store-buttons__button--${mode}`}
          type={TYPES.ICON}
          iconSvg={<GooglePlayLogo color={color} />}
        />
      )}
      {ios && (
        <Button
          url={linkUrl || URL_APPLE_APP_STORE}
          directLink
          className={`app-store-buttons__button app-store-buttons__button--${mode}`}
          type={TYPES.ICON}
          iconSvg={<AppStoreLogo color={color} />}
        />
      )}
      {!android && !ios && (
        <div className="app-store-buttons__container">
          {isLoggedIn ? (
            <Button
              url={linkUrl || URL_APPLE_APP_STORE}
              directLink
              type={TYPES.GHOST}
              className="app-store-buttons__button-desktop"
            >
              <div
                className="app-store-buttons__qr-code app-store-buttons__qr-code--with-auth"
                style={{ backgroundImage: `url(${qrCode})` }}
              >
                {!qrCode && <Sherpa type={TYPE_SMALL} />}
              </div>
            </Button>
          ) : (
            <>
              <Button
                url={URL_APPLE_APP_STORE}
                directLink
                type={TYPES.GHOST}
                className="app-store-buttons__button-desktop"
              >
                <div className="app-store-buttons__qr-code app-store-buttons__qr-code--app-store"/>
                <AppStoreLogo color="#151515"/>
              </Button>
              <Button
                url={URL_ANDROID_APP_STORE}
                directLink
                type={TYPES.GHOST}
                className="app-store-buttons__button-desktop"
              >
                <div className="app-store-buttons__qr-code app-store-buttons__qr-code--google-play"/>
                <GooglePlayLogo color="#151515"/>
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default connectRedux((state) => ({
  auth: state.auth,
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    setEventDefault: actions.eventTracking.setEventDefault,
  }
})(AppStoreButtons)
